/**
 * Date formatting for the audit log. Evidence needs absolute times, not
 * "2 hours ago" — so the rail shows a clock time under a day heading, and the
 * detail pane shows the full stamp including seconds and timezone.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Local-midnight day index, used to bucket events and to spot today/yesterday. */
export function dayKey(date: Date): number {
  return Math.floor(
    (date.getTime() - date.getTimezoneOffset() * 60 * 1000) / MS_PER_DAY,
  );
}

/** `TODAY` / `YESTERDAY` / `MON, JUL 28` — year appended outside this year. */
export function formatDayLabel(date: Date, now: Date): string {
  const delta = dayKey(now) - dayKey(date);
  if (delta === 0) return "Today";
  if (delta === 1) return "Yesterday";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

/** `9:41 AM` */
export function formatClockTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** `Jul 30, 2026` — always with the year, never relative to today. */
export function formatCalendarDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** `July 30, 2026` — the month spelled out, always with the year. */
export function formatLongCalendarDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** `Jul 30` */
export function formatMonthDay(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** `Jul 30, 2026 · 9:41:07 AM GMT+1` */
export function formatFullTimestamp(date: Date): string {
  const day = formatCalendarDate(date);
  const time = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
  return `${day} · ${time}`;
}
